import { Button, Input, message, Select, TableProps } from "antd";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CorrectActtionIcon from "../../../../components/common/CorrectActtionIcon";
import CrossActionIcon from "../../../../components/common/CrossActionIcon";
import TitleBar from "../../../../components/common/TitleBar";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { SurveyAnswer } from "../../../../features/organization/survey/survey.type";
import {
  useGetSurveyResponsesByMemberQuery,
  useUpdateSurveyAnswersStatusMutation,
} from "../../../../features/organization/survey/surveyApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { countParPageSerialNumber } from "../../../../utils/number";
import { valueOrNa } from "../../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";
import SurveyAnswersModal from "./_components/SurveyAnswersModal";

type TableStatus = {
  status: string;
};
type MDType = { type: string; mode: string; id?: number };
export default function SurveyDataDetailsSingle() {
  const [selectedData, setSelectedData] = useState<(string | number)[]>([]);
  const businessId = useAppSelector((state) => state.auth.user?.business_id);
  const [updatingStatus, setUpdatingStatus] = useState<string>();
  const { template_id, member_id } = useParams();
  const { open, handleOpen, handleClose } = useModal<unknown, "CONFIRM">();

  const [searchParams] = useSearchParams();
  const bondhuName = searchParams.get("bondhu");

  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<TableStatus>();

  // Queries
  const { data, isError, isLoading, isFetching, error } =
    useGetSurveyResponsesByMemberQuery(
      {
        params: {
          business_id: businessId as number,
          survey_template_id: template_id as string,
          business_member_id: member_id as string,
          search: keyword,
          ...filter,
        },
      },
      { skip: !businessId }
    );
  const surveyData = data?.data?.responses;

  // Mutations
  const [
    updateStatus,
    {
      isLoading: updating,
      isError: isUpdateError,
      error: updateError,
      isSuccess,
    },
  ] = useUpdateSurveyAnswersStatusMutation();

  useError(isError, error, "Survey answers get failed.");
  useError(isUpdateError, updateError, "Survey status update failed.");

  const handleUpdateStatus = (action: string, id?: number[]) => {
    updateStatus({ body: { ids: id, status: action } });
    setUpdatingStatus(action);
  };

  useEffect(() => {
    if (isSuccess) {
      setUpdatingStatus(undefined);
      setSelectedData([]);
      handleClose();
      message.success(
        `Answers ${updatingStatus === "accepted" ? "Accepted" : "Rejected"}.`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Answer ID",
      dataIndex: "id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Survey Date",
      align: "center",
      dataIndex: "created_at",
      render: (val) => (
        <span className="block text-nowrap">{formatDate(val)}</span>
      ),
    },
    {
      title: "Response",
      align: "center",
      render: (val) => (
        <button
          className="underline underline-offset-2"
          onClick={() => handleOpen({ type: "VIEW", data: val })}
        >
          View
        </button>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "status",
      render: (val: string) => {
        if (!val) return "n/a";

        const statusMap = {
          pending: {
            bg: "bg-yellow-50",
            text: "text-yellow-500",
            label: "Pending",
          },
          reject: {
            bg: "bg-red-50",
            text: "text-red-500",
            label: "Reject",
          },
          accepted: {
            bg: "bg-green-500/10",
            text: "text-green-600",
            label: "Accepted",
          },
        };

        const status = statusMap[val.toLowerCase() as keyof typeof statusMap];

        if (!status) return valueOrNa(val);

        return (
          <div
            className={`px-2 py-0.5 ${status.bg} rounded-md inline-flex justify-center items-center gap-2.5`}
          >
            <div
              className={`justify-start ${status.text} text-sm font-medium font-['Inter'] leading-normal`}
            >
              {status.label}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (val) =>
        val?.status === "pending" ? (
          <div className="w-full flex justify-center items-center gap-4">
            <button
              onClick={() => {
                handleOpen({
                  type: "CONFIRM",
                  data: { type: "accepted", mode: "single", id: val?.id },
                });
              }}
              className="cursor-pointer"
            >
              <CorrectActtionIcon />
            </button>

            <button
              onClick={() => {
                handleOpen({
                  type: "CONFIRM",
                  data: { type: "reject", mode: "single", id: val?.id },
                });
              }}
              className="cursor-pointer"
            >
              <CrossActionIcon />
            </button>
          </div>
        ) : null,
    },
  ];
  let title = "Accept Answer";
  let desc = "Are sure you want to accept this answer?";
  const modalData = open?.data as MDType;

  if (modalData?.type === "accepted") {
    if (modalData?.mode === "bulk") {
      title = "Accept Answers";
      desc = "Are sure you want to accept these answers?";
    }
  } else if (modalData?.type === "reject") {
    if (modalData?.mode === "bulk") {
      title = "Reject Answers";
      desc = "Are sure you want to reject these answers?";
    } else if (modalData?.mode === "single") {
      title = "Reject Answer";
      desc = "Are sure you want to reject this answer?";
    }
  }
  return (
    <>
      {open.type === "VIEW" && (
        <SurveyAnswersModal
          open={open.open}
          data={open.data as unknown as SurveyAnswer}
          handleClose={handleClose}
        />
      )}
      {open.type === "CONFIRM" && (
        <ConfirmModal
          subTitle={desc}
          handleClose={handleClose}
          open={open?.open}
          data={open?.data}
          okLoading={updating}
          okText={modalData?.type === "accepted" ? "Accept" : "Reject"}
          title={title}
          handleOk={() =>
            handleUpdateStatus(
              (open?.data as MDType)?.type,
              modalData?.id ? [modalData?.id] : (selectedData as number[])
            )
          }
        />
      )}
      <SidebarLayout title="Survey">
        <TitleBar title={(bondhuName || "") as string} backLink={"-1"} />

        <CustomTable
          loading={isLoading || isFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.total}
          dataSource={addKeyInArray(surveyData || [])}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as string[]);
            },
            getCheckboxProps: (record) => {
              return {
                disabled: record?.status !== "pending",
              };
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              {selectedData?.length !== 0 && (
                <>
                  <Button
                    onClick={() =>
                      handleOpen({
                        type: "CONFIRM",
                        data: { type: "accepted", mode: "bulk" },
                      })
                    }
                    // loading={updating && updatingStatus === "accepted"}
                    size="middle"
                    className="w-full bg-[#F2FFF9] !text-[#1DB469] !border-[#1DB469] hover:bg-[#F2FFF9] !hover:border-[#1DB469]"
                  >
                    Accept
                  </Button>
                  <Button
                    loading={updating && updatingStatus === "reject"}
                    onClick={() =>
                      handleOpen({
                        type: "CONFIRM",
                        data: { type: "reject", mode: "bulk" },
                      })
                    }
                    size="middle"
                    className="w-full bg-[#FFF5F4] !text-[#FF574D] !border-[#FFC5C1] hover:bg-[#FFF5F4] !hover:border-[#FFC5C1]"
                  >
                    Reject
                  </Button>
                </>
              )}

              <Input
                suffix={<SearchIcon />}
                placeholder="Search by Answer ID"
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={(val) => handleSearch(val)}
              />

              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={statusList}
                className="min-w-[90px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.status}
                onChange={(val) => handleFilter("status", val)}
                popupMatchSelectWidth={120}
              />
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}

const statusList = [
  {
    label: "Accepted",
    value: "accepted",
  },
  {
    label: "Reject",
    value: "reject",
  },
  {
    label: "Pending",
    value: "pending",
  },
];
